import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { HeroSolidBGView, type HeroSolidBGContentProps } from './HeroSolidBG';

export function VirtualTourHero(props: HeroSolidBGContentProps) {
  const bgDefinition = contentSchema.slotGroups!['virtualtourhero.bg_image'];
  const ctaDefinition = contentSchema.slotGroups!['virtualtourhero.cta_link'];
  return (
    <>
      <HeroSolidBGView {...props} prefix="virtualtourhero" bgDefinition={bgDefinition} />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent path="virtualtourhero.title" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableToken path="virtualtourhero.subtitle" type="richtext" label="Subtitle" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent path="virtualtourhero.blurb" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="virtualtourhero.bg_image" definition={bgDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="virtualtourhero.cta_link" definition={ctaDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourHero',
  displayName: 'Virtual Tour Hero',
  tokens: {
    'virtualtourhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Virtual Tour</p>',
    },
    'virtualtourhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Virtual Apartment Tours in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'virtualtourhero.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultField: 'property_virtualtours_blurb',
    },
  },
  slotGroups: {
    'virtualtourhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Apartment interior',
        },
      },
      generator: {
        prompt:
          'Pick the hero background photo for the Virtual Tour page: an attractive apartment interior at this property that represents the spaces shown in the tours. Write alt text describing what the photo shows.',
        context:
          'Full-bleed background hero at the top of the Virtual Tour page, behind a semi-opaque primary-color overlay with centered white headline text. Prefer staged or professional photos; skip blurry, dark, cluttered, or unappealing shots. Priority: relevance to the toured apartment interiors first, image quality second, uniqueness third. Each inner page (Floor Plans, Amenities, Gallery, Virtual Tour, Neighborhood, Contact Us) has its own hero; the Floor Plans hero also uses an interior, so pick a different room or photo from it. Do not reuse an image already used as another page hero or the homepage hero unless no other suitable image exists.',
      },
    },
    'virtualtourhero.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(VirtualTourHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourHero;
